import {
  MutableRefObject,
  ReactElement,
  forwardRef,
  useContext,
  useRef,
  ComponentProps,
} from "react"
import ReactSelect from "react-select"
import type { GroupBase, Props, SelectInstance } from "react-select"
import { AdjacentContainer } from "./components/containers"
import { useSelectProps } from "./use-select-props"
import { ModalContext } from "../modal"
import ReactAsyncSelect, { AsyncProps } from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable'

const Select = forwardRef(
  <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
    props: Props<Option, IsMulti, Group> &
      AsyncProps<Option, IsMulti, Group> &
      ComponentProps<typeof AsyncCreatableSelect>,
    ref:
      | ((instance: SelectInstance<Option, IsMulti, Group> | null) => void)
      | MutableRefObject<SelectInstance<Option, IsMulti, Group> | null>
      | null
  ) => {
    // Async options
    const { loadOptions, defaultOptions, onCreateOption, ...rest } = props
    const { className, ...selectProps } = useSelectProps(rest)

    const { label, required, name } = selectProps
    const containerRef = useRef<HTMLDivElement>(null)

    const { portalRef } = useContext(ModalContext)

    const baseProps = {
      ...selectProps,
      ["aria-labelledby"]: `${name}_label`,
      ref,
      name,
      menuPortalTarget:
        portalRef?.current ||
        (typeof document !== "undefined" ? document.body : null),
      menuShouldBlockScroll: true,
    }

    const asyncProps = {
      ...baseProps,
      defaultOptions,
      loadOptions,
    }

    return (
      <AdjacentContainer
        ref={containerRef}
        label={label}
        required={required}
        name={name}
        className={className}
      >
        {loadOptions ?
          onCreateOption ? (
            <AsyncCreatableSelect
              {...asyncProps}
              onCreateOption={onCreateOption}
            />
            ) : (
          <ReactAsyncSelect {...asyncProps}/>
        ) : (
          <ReactSelect {...baseProps}/>
        )}
      </AdjacentContainer>
    )
  }
)

export { Select }
